import { Outlet, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, ChevronLeftIcon } from 'lucide-react';
import { useEffect, useSyncExternalStore, type JSX } from 'react';

import { AppSheet } from '@/components/ui/app-sheet.js';
import { Button } from '@/components/ui/button.js';
import { SheetClose } from '@/components/ui/sheet.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.js';
import { ArtifactSidePanel } from '@/features/artifacts/ArtifactSidePanel.js';
import { CommandPalette } from '@/features/command-palette/CommandPalette.js';
import { FeedbackLauncher } from '@/features/feedback/FeedbackLauncher.js';
import { useLastLocationRecorder } from '@/features/last-location/useLastLocationRecorder.js';
import { GraphSearchNotice } from '@/features/onboarding/GraphSearchNotice.js';
import { HintBeaconLayer } from '@/features/onboarding/HintBeaconLayer.js';
import { OnboardingIntro } from '@/features/onboarding/OnboardingIntro.js';
import { ResetOnboardingEffect } from '@/features/onboarding/ResetOnboardingEffect.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import { useWorkspaceChatSwitch } from '@/features/workspace/useWorkspaceChatSwitch.js';
import { useMaintenance } from '@/hooks/useMaintenance.js';
import { useBreakpoint } from '@/hooks/useMediaQuery.js';
import { useVersionCheck } from '@/hooks/useVersionCheck.js';
import { useBroadcastChannelSync } from '@/integration/runtime/broadcast-channel.js';
import { useTeamsDeepLink } from '@/integration/runtime/teams-deep-link.js';
import { useTeamsResumeRefresh } from '@/integration/runtime/useTeamsResumeRefresh.js';
import { useUserEventStream } from '@/integration/runtime/user-event-stream.js';
import { useChatSessionStore } from '@/integration/state/chat-session-store.js';
import { isPublicViewPath } from '@/lib/share-path.js';
import { conversationQueryOptions } from '@/lib/api/conversations.js';
import {
  getState as getIdentityState,
  subscribe as subscribeIdentity,
} from '@/store/identity-store.js';

import { MaintenanceScreen } from './gates/MaintenanceScreen.js';
import { Header } from './Header.js';
import { MaintenanceBanner } from './MaintenanceBanner.js';
import { Sidebar } from './Sidebar.js';

// Edge-swipe constants for opening the mobile drawer.
// EDGE_ZONE_PX: how close to the left edge the touch must start.
// MIN_HORIZONTAL_DELTA_PX: minimum rightward distance to fire (eats Android
//   system back-gesture which fires on much smaller distances at the edge).
// MAX_DURATION_MS: swipe must complete within this — slow drags don't count.
// VERTICAL_TOLERANCE_RATIO: |dy| must be less than |dx| * this — predominantly
//   horizontal motion only.
const EDGE_ZONE_PX = 24;
const MIN_HORIZONTAL_DELTA_PX = 60;
const MAX_DURATION_MS = 500;
const VERTICAL_TOLERANCE_RATIO = 0.6;

/**
 * Routes that render chrome-free (no sidebar, no header) even though they sit behind the sign-in gate.
 * The public view paths are chrome-free too, but they are not listed here: `isPublicViewPath` already
 * owns that rule for the whole app, and one list claiming it as well is how the two drift apart.
 */
const BARE_ROUTES = ['/access-denied', '/blocked', '/game', '/secretary'];

/**
 * Routes whose CHILDREN are bare while the parent keeps the shell: `/reports` is the section's own
 * page, `/reports/:runId` is one insights report that fills the window.
 */
const BARE_CHILD_ROUTES = ['/reports'];

/** A path is bare when it equals a bare route, nests under one (`/game/...`), or is a bare child. */
function isBareRoutePath(pathname: string): boolean {
  return (
    BARE_ROUTES.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`),
    ) || BARE_CHILD_ROUTES.some((route) => pathname.startsWith(`${route}/`))
  );
}

// rem/vw bounds: floor 15rem (240px) keeps sidebar usable on tablet/small
// desktop. From ~1200 px the sidebar starts growing at 20vw, capped at
// min(20rem, 24vw) so ultra-wide layouts (4K/8K) don't get an arm-wide rail
// (Linear / Notion / ChatGPT all settle around 16-20rem). Was 32rem before
// Phase 3; tightened so sidebar share stays bounded on 5K+.
const SIDEBAR_WIDTH = 'clamp(15rem, 20vw, min(20rem, 24vw))';
// 4rem collapses gracefully: 13px → 52px, 26px → 104px, 48px → 192px.
const SIDEBAR_WIDTH_COLLAPSED = '4rem';

export function AppShell(): JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isBareRoute = isPublicViewPath(pathname) || isBareRoutePath(pathname);
  /** The artifact rail belongs to the chat only — see where it renders below. */
  const isChatRoute = pathname === '/chats' || pathname.startsWith('/chats/');
  const selectedWorkspaceId = useUiStore((s) => s.selectedWorkspaceId);
  const activeConversationId = useChatSessionStore(
    (s) => s.activeConversationId,
  );
  const activeConversationQuery = useQuery({
    ...conversationQueryOptions(activeConversationId ?? ''),
    enabled: isChatRoute && activeConversationId !== null,
  });
  const chatArtifactReadOnly =
    activeConversationId !== null &&
    (activeConversationQuery.isPending ||
      activeConversationQuery.isError ||
      activeConversationQuery.data?.status === 'archived');
  /**
   * A public view path loads unauthenticated and outside Teams. Keep the Teams-SDK / authenticated
   * side-effect hooks inert there so they never call `app.initialize()` (which hangs outside Teams) nor
   * open the gated event stream. Hook order is unchanged — the flag no-ops the effects.
   */
  const sideEffectsEnabled =
    !isPublicViewPath(pathname) && !pathname.startsWith('/secretary/');

  useBroadcastChannelSync();
  useUserEventStream(sideEffectsEnabled);
  // Teams activity-feed deep-link handler — if we were opened from a
  // bell-icon notification with a subEntityId, jump to /chats?conversationId=…
  // immediately on mount.
  useTeamsDeepLink(sideEffectsEnabled);
  useTeamsResumeRefresh(sideEffectsEnabled);
  useLastLocationRecorder(sideEffectsEnabled);
  // On a user workspace switch outside `/chats`: clear stale chat session ids (`ChatsPage` owns `/chats`).
  useWorkspaceChatSwitch();
  const versionBanner = useVersionCheck();

  const breakpoint = useBreakpoint();
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const mobileDrawerOpen = useUiStore((s) => s.mobileDrawerOpen);
  const setMobileDrawerOpen = useUiStore((s) => s.setMobileDrawerOpen);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);

  // Maintenance gate state. Polled in `useMaintenance` (10s when active,
  // 60s otherwise). Admin bypass uses the identity store's roles claim —
  // matches the same source `Sidebar` uses for its admin-only menu.
  const maintenance = useMaintenance();
  const identity = useSyncExternalStore(
    subscribeIdentity,
    getIdentityState,
    getIdentityState,
  );
  const isAdmin = (identity.identity?.roles ?? []).includes('admin');
  const identityLoaded = identity.loaded;

  // Sidebar stays visible from `tablet` (≥768 px) — only `mobile` collapses it
  // to a Sheet drawer. Previously this required `desktop` (≥1024 px), which felt
  // like the sidebar disappeared too early when shrinking the window.
  const isDesktop = breakpoint !== 'mobile';

  // Edge-swipe-to-open for the mobile drawer. The drawer is a Radix Dialog —
  // it has slide-to-close once open, but no built-in open-on-edge-swipe.
  // Without this, users swiping from the left edge get whatever the host
  // (mobile Teams / iOS back-gesture / Android predictive back) does, which
  // appears as a blank screen. We capture the gesture early and open the
  // drawer ourselves.
  useEffect(() => {
    if (isDesktop || isBareRoute) return;
    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let armed = false;

    const onStart = (e: TouchEvent): void => {
      if (mobileDrawerOpen) return;
      const t = e.touches[0];
      if (!t) return;
      if (t.clientX > EDGE_ZONE_PX) return;
      startX = t.clientX;
      startY = t.clientY;
      startTime = Date.now();
      armed = true;
    };

    const onMove = (e: TouchEvent): void => {
      if (!armed) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      if (Date.now() - startTime > MAX_DURATION_MS) {
        armed = false;
        return;
      }
      if (dy > Math.abs(dx) * VERTICAL_TOLERANCE_RATIO) {
        // Predominantly vertical — let the user scroll.
        armed = false;
        return;
      }
      if (dx >= MIN_HORIZONTAL_DELTA_PX) {
        armed = false;
        setMobileDrawerOpen(true);
      }
    };

    const onEnd = (): void => {
      armed = false;
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [isDesktop, isBareRoute, mobileDrawerOpen, setMobileDrawerOpen]);

  if (isBareRoute) {
    return <Outlet />;
  }

  // Maintenance gate: when maintenance is on, swap to the downtime screen
  // unless the current user is an admin AND the toggle allows admins.
  //
  // We deliberately WAIT for identity to resolve before deciding. Without
  // the `identityLoaded` guard, the very first render after a refresh has
  // `identity.identity === null` so an admin briefly fails the bypass
  // check and flashes the downtime screen for the ~500 ms it takes
  // `/v1/me` to resolve. While identity is loading we render nothing
  // (the bare `<TeamsOnlyGate>` background stays visible) — much calmer
  // than a maintenance-flash → chat-flash sequence.
  if (maintenance.enabled) {
    if (!identityLoaded) {
      return <></>;
    }
    if (!(isAdmin && maintenance.allowAdmins)) {
      return <MaintenanceScreen status={maintenance} />;
    }
  }

  return (
    <div
      className="app-surface flex w-full overflow-hidden text-foreground antialiased"
      style={{ height: 'var(--app-height, 100dvh)' }}
    >
      {isDesktop && (
        <div
          className="relative z-10 shrink-0"
          style={{
            width: sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH,
            transition: sidebarCollapsed
              ? 'width 0.3s cubic-bezier(0.22, 1, 0.36, 1) 0.06s'
              : 'width 0.3s cubic-bezier(0.22, 1, 0.36, 1) 0.06s',
          }}
        >
          <aside className="h-full overflow-hidden border-r border-sidebar-border bg-sidebar">
            <Sidebar collapsed={sidebarCollapsed} hideCollapseButton />
          </aside>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={toggleSidebar}
                className="sidebar-rail-toggle"
                aria-label={
                  sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
                }
              >
                <ChevronLeftIcon
                  className="size-5 transition-transform duration-200"
                  style={{
                    transform: sidebarCollapsed ? 'rotate(180deg)' : 'none',
                  }}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {sidebarCollapsed ? 'Expand' : 'Collapse'}
            </TooltipContent>
          </Tooltip>
        </div>
      )}

      {!isDesktop && (
        <AppSheet
          open={mobileDrawerOpen}
          onOpenChange={setMobileDrawerOpen}
          title="Navigation"
          titleHidden
          side="left"
          className="w-[min(88vw,22rem)] border-sidebar-border bg-sidebar p-0"
          showCloseButton={false}
        >
          <SheetClose asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="absolute right-3 top-4 z-10 text-muted-foreground/60"
              aria-label="Close sidebar"
            >
              <ArrowLeftIcon className="size-[1.125rem]" />
            </Button>
          </SheetClose>
          <Sidebar
            collapsed={false}
            hideCollapseButton
            onNavigate={() => setMobileDrawerOpen(false)}
          />
        </AppSheet>
      )}

      {/* No maxInlineSize cap here — Header + main span full viewport width
          (minus sidebar) so the theme toggle and overflow scrollbar align with
          the right edge of the screen. Per-route content (chat-thread-column,
          home-fluid, etc.) applies its own readable-width cap, so this only
          frees the chrome — message bubbles stay centred. */}
      {/* `relative` anchors the feedback launcher below to this column's
          bottom-right corner instead of the viewport's — the artifact rail is a
          sibling column occupying that same corner of the screen. */}
      <div className="relative flex w-full min-w-0 flex-1 flex-col">
        {maintenance.enabled && isAdmin ? (
          <MaintenanceBanner status={maintenance} />
        ) : null}
        <Header />
        <main className="relative min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
          <Outlet />
        </main>
        <FeedbackLauncher
          identity={identity.identity}
          workspaceId={selectedWorkspaceId}
          conversationId={activeConversationId}
        />
      </div>

      {/* The artifact panel is a full-height rail here, a sibling of the chat column rather than
          content inside it — so it mirrors the left sidebar and the Header stays with the chat
          instead of running above the panel. Rendered only on the chat route: it is chat-scoped, and
          a panel left open would otherwise follow the user onto every other page. */}
      {isChatRoute && (
        <ArtifactSidePanel
          workspaceId={selectedWorkspaceId}
          conversationId={activeConversationId}
          readOnly={chatArtifactReadOnly}
        />
      )}

      <CommandPalette />
      <OnboardingIntro />
      <GraphSearchNotice />
      <ResetOnboardingEffect />
      <HintBeaconLayer />
      {versionBanner}
    </div>
  );
}
